import React from 'react'

import {
  $applyNodeReplacement,
  addClassNamesToElement,
  DecoratorNode,
  DOMConversionMap,
  DOMExportOutput,
  EditorConfig,
  LexicalEditor,
  LexicalNode,
  NodeKey
} from '@eastcoal/editor/exports'
import { setDomStyle } from '@eastcoal/editor/utils/style'

import { TableVariablePlaceholder } from '../../ui/TableVariablePlaceholder'

import {
  TABLE_VARIABLE_NODE_INDEX_COL_PROPERTY,
  TABLE_VARIABLE_NODE_INDEX_COL_TEXT
} from './constants'
import {
  SerializedTableVariableNode,
  TableVariableNodeIndexCol,
  TableVariableNodeProps,
  TableVariableNodeState
} from './interface'

const defaultTableStyleConfig: TableVariableNodeState['tableStyleConfig'] = {
  borderFormat: 'cell',
  textAlign: 'left',
  verticalAlign: 'middle'
}

const defaultHeaderStyleConfig: TableVariableNodeState['headerStyleConfig'] = {
  fontWeight: 'bold'
}

export class TableVariableNode extends DecoratorNode<React.JSX.Element> {
  private __variable: TableVariableNodeState['variable']
  private __tableStyleConfig: TableVariableNodeState['tableStyleConfig']
  private __headerStyleConfig: TableVariableNodeState['headerStyleConfig']
  private __bodyStyleConfig: TableVariableNodeState['bodyStyleConfig']

  constructor(
    {
      variable,
      tableStyleConfig,
      headerStyleConfig,
      bodyStyleConfig
    }: TableVariableNodeProps,
    key?: NodeKey
  ) {
    super(key)
    this.__variable = variable
    this.__tableStyleConfig = Object.assign(
      {},
      defaultTableStyleConfig,
      tableStyleConfig
    )
    this.__headerStyleConfig = Object.assign(
      {},
      defaultHeaderStyleConfig,
      headerStyleConfig
    )
    this.__bodyStyleConfig = Object.assign({}, bodyStyleConfig)
  }

  getVariable() {
    return this.__variable
  }

  getIndexCol() {
    return this.__variable.children.find(
      item => item.property === TABLE_VARIABLE_NODE_INDEX_COL_PROPERTY
    )
  }

  setIndexCol(indexCol: TableVariableNodeIndexCol) {
    this.getWritable().__variable = {
      ...this.__variable,
      children: [
        {
          id: TABLE_VARIABLE_NODE_INDEX_COL_PROPERTY,
          property: TABLE_VARIABLE_NODE_INDEX_COL_PROPERTY,
          name: indexCol.text || TABLE_VARIABLE_NODE_INDEX_COL_TEXT,
          width: '60px',
          description: '自动递增的序号'
        },
        ...this.__variable.children.filter(
          item => item.property !== TABLE_VARIABLE_NODE_INDEX_COL_PROPERTY
        )
      ]
    }
  }

  setFields(fields: TableVariableNodeState['variable']['children']) {
    this.getWritable().__variable = {
      ...this.__variable,
      children: fields
    }
  }

  setColWidths(colWidths: string[]) {
    this.getWritable().__variable = {
      ...this.__variable,
      children: this.__variable.children.map((item, index) => {
        return {
          ...item,
          width: colWidths[index]
        }
      })
    }
  }

  getTableStyleConfig() {
    return this.__tableStyleConfig || {}
  }

  setTableStyleConfig(tableStyle: TableVariableNodeState['tableStyleConfig']) {
    this.getWritable().__tableStyleConfig = Object.assign(
      {},
      this.__tableStyleConfig,
      tableStyle
    )
  }

  getHeaderStyleConfig() {
    return this.__headerStyleConfig || {}
  }

  setHeaderStyleConfig(
    headerStyle: TableVariableNodeState['headerStyleConfig']
  ) {
    this.getWritable().__headerStyleConfig = Object.assign(
      {},
      this.__headerStyleConfig,
      headerStyle
    )
  }

  getBodyStyleConfig() {
    return this.__bodyStyleConfig || {}
  }

  setBodyStyleConfig(bodyStyle: TableVariableNodeState['bodyStyleConfig']) {
    this.getWritable().__bodyStyleConfig = Object.assign(
      {},
      this.__bodyStyleConfig,
      bodyStyle
    )
  }

  static getType(): string {
    return 'table-variable'
  }

  static clone(node: TableVariableNode) {
    return new TableVariableNode(
      {
        variable: node.getVariable(),
        tableStyleConfig: node.getTableStyleConfig(),
        headerStyleConfig: node.getHeaderStyleConfig(),
        bodyStyleConfig: node.getBodyStyleConfig()
      },
      node.getKey()
    )
  }

  static importJSON(
    serializedNode: SerializedTableVariableNode
  ): TableVariableNode {
    return $createTableVariableNode(serializedNode).updateFromJSON(
      serializedNode
    )
  }

  exportJSON(): SerializedTableVariableNode {
    return {
      ...super.exportJSON(),
      type: this.getType(),
      variable: this.getVariable(),
      tableStyleConfig: this.getTableStyleConfig(),
      headerStyleConfig: this.getHeaderStyleConfig(),
      bodyStyleConfig: this.getBodyStyleConfig()
    }
  }

  static importDOM(): DOMConversionMap {
    return {
      table: () => ({
        priority: 0,
        conversion: tableDom => {
          if (
            tableDom.dataset.variableType === this.getType() &&
            tableDom.dataset.variable
          ) {
            return {
              node: $createTableVariableNode({
                variable: JSON.parse(tableDom.dataset.variable),
                tableStyleConfig: {},
                headerStyleConfig: {},
                bodyStyleConfig: {}
              })
            }
          }
          return null
        }
      })
    }
  }

  exportDOM(editor: LexicalEditor): DOMExportOutput {
    const varaible = this.getVariable()

    // 容器节点，用于设置预置的样式
    const createWrapperDom = () => {
      const wrapperDom = document.createElement('div')
      addClassNamesToElement(wrapperDom, editor._config.theme.tableVariable)
      return wrapperDom
    }

    // table 节点
    const createTableDom = () => {
      const { borderFormat, fontFamily, lineHeight, textAlign, verticalAlign } =
        this.getTableStyleConfig()
      const tableDom = document.createElement('table')
      // 以后需要复制粘贴功能再放开
      // tableDom.dataset.nodeType = this.getType()
      // tableDom.dataset.variable = JSON.stringify(varaible)
      addClassNamesToElement(
        tableDom,
        !!borderFormat && `border-${borderFormat}`,
        !!textAlign && `align-${textAlign}`,
        !!verticalAlign && `vertical-${verticalAlign}`
      )
      setDomStyle(tableDom, {
        fontFamily,
        lineHeight
      })
      return tableDom
    }

    // 表头节点
    const createHeaderDom = () => {
      const headerStyleConfig = this.getHeaderStyleConfig()
      const headerDom = document.createElement('thead')
      const headerRowDom = document.createElement('tr')
      for (let i = 0; i < varaible.children.length; i++) {
        const child = varaible.children[i]
        const colDom = document.createElement('th')
        colDom.style.width = child.width || 'auto'
        colDom.style.backgroundColor = headerStyleConfig.backgroundColor || ''
        setDomStyle(colDom, {
          // pdfhtml 要设置到 th 上才支持 BackgroundColor
          backgroundColor: headerStyleConfig.backgroundColor,
          // 单独设置的列样式
          width: child.width,
          textAlign: child.textAlign,
          fontWeight: child.fontWeight,
          color: child.color
        })
        colDom.innerHTML = `${child.name}`
        headerRowDom.append(colDom)
      }
      setDomStyle(headerDom, headerStyleConfig)
      headerDom.appendChild(headerRowDom)
      return headerDom
    }

    // 表格内容节点
    const createBodyDom = () => {
      const bodyDom = document.createElement('tbody')
      const rowDom = document.createElement('tr')
      rowDom.setAttribute('th:each', `item,stat : \${${varaible.property}}`)
      for (let i = 0; i < varaible.children.length; i++) {
        const child = varaible.children[i]
        const colDom = document.createElement('td')
        if (child.property === TABLE_VARIABLE_NODE_INDEX_COL_PROPERTY) {
          colDom.setAttribute('th:text', '${stat.count}')
        } else {
          colDom.setAttribute('th:text', `\${item.${child.property}}`)
        }
        setDomStyle(colDom, {
          // 单独设置的列样式
          textAlign: child.textAlign,
          fontWeight: child.fontWeight,
          color: child.color
        })
        rowDom.appendChild(colDom)
      }
      bodyDom.appendChild(rowDom)
      return bodyDom
    }

    // 创建表格
    const wrapperDom = createWrapperDom()
    const tableDom = createTableDom()
    const headerDom = createHeaderDom()
    const bodyDom = createBodyDom()
    tableDom.appendChild(headerDom)
    tableDom.appendChild(bodyDom)
    wrapperDom.appendChild(tableDom)
    return {
      element: wrapperDom
    }
  }

  updateDOM() {
    return false
  }

  createDOM(config: EditorConfig, editor?: LexicalEditor): HTMLElement {
    const dom = document.createElement('div')
    addClassNamesToElement(dom, config.theme.tableVariable)
    return dom
  }

  decorate(): React.JSX.Element {
    return (
      <TableVariablePlaceholder
        bodyStyleConfig={this.getBodyStyleConfig()}
        headerStyleConfig={this.getHeaderStyleConfig()}
        nodeKey={this.getKey()}
        tableStyleConfig={this.getTableStyleConfig()}
        variable={this.getVariable()}
      />
    )
  }

  isInline(): boolean {
    return false
  }

  isKeyboardSelectable(): boolean {
    return true
  }

  isIsolated(): boolean {
    return false
  }
}

export const $isTableVariableNode = (
  node: LexicalNode | null
): node is TableVariableNode => {
  return node instanceof TableVariableNode
}

export const $createTableVariableNode = (props: TableVariableNodeProps) => {
  return $applyNodeReplacement(new TableVariableNode(props))
}
